import { useEffect, useRef, useState } from 'react';
import type {
  FindingsMessage,
  MeterFrame,
  PairRef,
  ProbeInfo,
  SpectrumMessage,
  StreamState,
  TransportMessage,
  VideoMessage,
} from '@shared/protocol';
import { socket, type PairsFrame } from './socket';

/** Ring buffer of goniometer sample pairs, read by the canvas without React re-renders. */
export class PairRing {
  readonly capacity: number;
  readonly data: Float32Array;
  written = 0; // total pairs ever written
  rate = 6000;

  constructor(capacity = 8192) {
    this.capacity = capacity;
    this.data = new Float32Array(capacity * 2);
  }

  push(frame: PairsFrame): void {
    this.rate = frame.rate;
    const n = frame.samples.length / 2;
    for (let i = 0; i < n; i++) {
      const p = (this.written % this.capacity) * 2;
      this.data[p] = frame.samples[i * 2];
      this.data[p + 1] = frame.samples[i * 2 + 1];
      this.written++;
    }
  }
}

export interface LiveRefs {
  /** Latest meter frame, updated at ~10 Hz without re-rendering. */
  frame: MeterFrame | null;
  frameAt: number;
  spectrum: SpectrumMessage | null;
  pairs: PairRing;
}

/** Transport and video samples kept for the graphs (last hour, 1 Hz). */
export interface TransportPoint {
  t: number;
  rtt: number | null;
  loss: number | null;
  rx: number | null;
  buffer: number | null;
  latency: number | null;
  age: number | null;
  bitrate: number | null;
}

/**
 * Subscribes to a stream over the shared WebSocket. Canvas components read
 * `live.current` every animation frame; React state carries the slower-changing
 * status, probe, findings, video/transport and a throttled copy of the frame.
 */
export function useStreamLive(streamId: number, pair: PairRef) {
  const live = useRef<LiveRefs>({ frame: null, frameAt: 0, spectrum: null, pairs: new PairRing() });
  const [status, setStatus] = useState<StreamState | null>(null);
  const [probe, setProbe] = useState<ProbeInfo | null>(null);
  const [frame, setFrame] = useState<MeterFrame | null>(null);
  const [findings, setFindings] = useState<FindingsMessage | null>(null);
  const [video, setVideo] = useState<VideoMessage['video'] | null>(null);
  const [transport, setTransport] = useState<TransportMessage | null>(null);
  const [connected, setConnected] = useState(socket.connected);
  const transportSeries = useRef<TransportPoint[]>([]);
  const { track, a, b } = pair;

  // Subscription lifetime follows the stream; pair changes are re-subscriptions.
  useEffect(() => {
    socket.start();
    let lastState = 0;
    transportSeries.current = [];
    const offMsg = socket.onMessage((msg) => {
      if (!('streamId' in msg) || msg.streamId !== streamId) return;
      switch (msg.type) {
        case 'meter': {
          live.current.frame = msg;
          live.current.frameAt = performance.now();
          const now = performance.now();
          // Numbers in React at ~4 Hz are easier to read than at 10 Hz.
          if (now - lastState > 250) {
            lastState = now;
            setFrame(msg);
          }
          break;
        }
        case 'spectrum':
          live.current.spectrum = msg;
          break;
        case 'status':
          setStatus(msg.state);
          if (msg.state.status !== 'live' && msg.state.status !== 'stalled') live.current.frame = null;
          break;
        case 'probe':
          setProbe(msg.probe);
          break;
        case 'findings':
          setFindings(msg);
          break;
        case 'video':
          setVideo(msg.video);
          break;
        case 'transport': {
          setTransport(msg);
          const s = transportSeries.current;
          s.push({
            t: msg.t,
            rtt: msg.srt?.connected ? msg.srt.rttMs : null,
            loss: msg.srt?.connected ? msg.srt.lossPercent : null,
            rx: msg.srt?.receiveMbps ?? null,
            buffer: msg.srt?.bufferMs ?? null,
            latency: msg.hls?.latency ?? null,
            age: msg.hls?.sinceNewSegment ?? null,
            bitrate: null,
          });
          if (s.length > 3600) s.shift();
          break;
        }
      }
    });
    const offConn = socket.onConnection(setConnected);
    return () => {
      offMsg();
      offConn();
    };
  }, [streamId]);

  useEffect(() => {
    live.current.pairs = new PairRing();
    const offPairs = socket.onPairs((f) => {
      if (f.streamId === streamId && f.track === track && f.a === a && f.b === b) live.current.pairs.push(f);
    });
    const unsubscribe = socket.subscribe(streamId, { track, a, b });
    return () => {
      offPairs();
      unsubscribe();
    };
  }, [streamId, track, a, b]);

  return { live, status, probe, frame, findings, video, transport, transportSeries, connected };
}
